import React, { useCallback, useEffect, useState } from 'react';
import CardShell from '../../components/CardShell';
import { Cell, Choix } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { getCostsView, setCostAlign, setCostForecast, setStockVarMode, updateCostLine } from '../../api/opsCosts';
import { MethodChip } from '../ProfitLossPage';
import '../ForecastCashPlan.css';
import './PurchasesMonthlyCard.css';

/* CHARGES DIRECTES et CHARGES VARIABLES (2026-09-29), deux sections de
   Ventes & Ops › Opérations servies par cette carte (`part`). Charges
   directes : le chiffre d'affaires et le coût des ventes calculé, puis UNE
   carte « Charges directes » (coûts des produits vendus, autres coûts
   directs). Charges variables : le coût variable d'exploitation. Historique,
   ex-« Achats & stock » puis « Coûts variables », quatre cartes :
   1. le chiffre d'affaires, qui se déplie d'un clic sur sa ligne, et le coût
      des ventes calculé du plan des ventes, un repère ;
   2. les coûts des produits vendus, à la structure du plan comptable (Achats,
      Variation de stock, Sous-traitance…), ALIGNÉS ou non sur ce calcul
      (bascule posée sur leur total, 2026-09-29) : alignés, la Variation de stock se
      calcule ; libres, tout se tape, pour un plan dont la marge paraît trop
      belle ;
   3. les autres coûts directs ;
   4. les coûts variables d'exploitation : autant de lignes qu'on veut (aucune,
      dix, cent), prises dans les autres charges d'exploitation et liées au
      revenu (frais de distribution et d'envoi…) ; « ＋ Ajouter » en pose une,
      la croix la retire (drapeau `variable_cost`, 2026-10-22).
   Sous chaque total, sa part du chiffre d'affaires.
   Chaque ligne de coût porte sa RÈGLE, la même que dans le compte de résultat
   (2026-09-29) : Manuel, Moyenne YTD, % des revenus (d'une ligne de produits
   ou du chiffre d'affaires net entier, le compte suivant le taux) ou Cible. Une autre règle déjà posée reste lisible.
   Les lignes sont celles du compte de résultat : une seule donnée, lue aussi
   par le Résultat prévisionnel et le plan de liquidité. Une ligne en saisie se
   tape (fond jaune) ; une ligne sous règle se lit avec sa règle en gris, et y
   taper un mois la fige et la passe en saisie ; la Variation de stock se
   calcule (Achats + Variation = coût du plan). Signes du compte de résultat.

   DEUX VUES (2026-09-29) : `pro` montre les douze mois, le RÉALISÉ de Finance
   devant le prévisionnel, pour projeter contre l'historique ; sans lui, les
   seuls mois à venir. La même carte sert Revenus & Marges dans le Résultat
   prévisionnel, en vue pro : une seule source, un seul écran. */

/* Les modes des coûts (2026-09-29) : Manuel, Moyenne YTD, % des revenus, Cible,
   et Vide (2026-09-30) : la ligne qu'on ne planifie pas vaut 0, sans fond de saisie. */
const CHOICES = ['', 'avg_ytd', 'pct_of', 'target', 'zero'];
const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const RULE = {
  last_actual: 'dernier mois', avg_ytd: 'moyenne YTD', n1_seasonal: 'saisonnalisé N-1',
  pct_of: "% d'une ligne", target: 'cible', sales_offers: 'plan des ventes', zero: 'vide',
  fixed_charges: 'charges fixes',
};

export default function PurchasesMonthlyCard({ activeTenant, year, unit = 'kchf', pro = false, canWrite = true, part = 'direct' }) {
  const p = useP();
  const tid = activeTenant?.id;
  const [view, setView] = useState(null);
  const [caOpen, setCaOpen] = useState(false);
  const load = useCallback(() => {
    if (!tid || !year) return;
    getCostsView(tid, year).then((r) => setView(r && !r.error ? r.data : null)).catch(() => setView(null));
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  if (!tid) return null;
  if (!view) return <p className="fcp-loading">{p('Chargement…')}</p>;
  const closed = Number(view.closed_count) || 0;
  const months = pro ? Array.from({ length: 12 }, (_, i) => i + 1)
    : Array.from({ length: 12 - closed }, (_, i) => closed + i + 1);
  // Un filet marque la frontière du réalisé et du prévisionnel.
  const split = pro && closed > 0 && closed < 12;
  const edge = (m) => (split && m === closed + 1 ? ' pmc-edge' : '');
  const div = unit === 'chf' ? 1 : 1000;
  const uLbl = unit === 'chf' ? 'CHF' : 'kCHF';
  const fmt = (v) => (v == null || Math.abs(Number(v)) < 0.5 ? '–' : Math.round(Number(v) / div).toLocaleString('de-CH'));
  const at = (r, m) => Number(r?.months?.[String(m)] || 0);
  const meta = Object.fromEntries((view.lines || []).map((l) => [l.id, l]));
  const method = Object.fromEntries((view.lines || []).map((l) => [l.id, l.forecast_method || '']));
  const rows = view.rows || [];
  const details = rows.filter((r) => r.type === 'detail');
  const revTotal = rows.find((r) => r.type === 'section_total' && r.code === 'sec:revenue');
  const revLines = details.filter((r) => r.section === 'revenue');
  const goods = details.filter((r) => r.section === 'cogs' && r.group === 'cogs_goods');
  const others = details.filter((r) => r.section === 'cogs' && r.group === 'cogs_other');
  const opexLines = (view.lines || []).filter((l) => l.section === 'other_opex' && !l.hidden);
  const varLines = opexLines.filter((l) => l.variable_cost);
  const varRows = varLines.map((l) => details.find((r) => r.code === l.id)).filter(Boolean);
  const libres = opexLines.filter((l) => !l.variable_cost);
  const locked = (r) => !!(r.sales_locked || r.people_locked || r.capex_locked || r.budget_locked || r.computed);
  const sumOf = (list, m) => list.reduce((s, r) => s + at(r, m), 0);
  const hasPlanCost = (view.plan_cogs || []).some((v) => Math.abs(Number(v) || 0) > 0.5);
  const total = (f) => months.reduce((s, m) => s + f(m), 0);
  const save = (r, m, x) => setCostForecast(tid, {
    year, reporting_line_id: r.code, month: m, amount: x == null ? 0 : -Math.abs(x) * div,
  }).then(() => load()).catch(() => {});

  const head = (
    <thead>
      {split && (
        <tr className="pmc-bands">
          <th />
          <th className="lk-band lk-band--real" colSpan={closed}>{p('Réalisé')}</th>
          <th className="lk-band lk-band--fore" colSpan={12 - closed}>{p('Prévisionnel')}</th>
          <th />
        </tr>
      )}
      <tr>
        <th className="fcp-lbl" />
        {months.map((m) => <th key={m} className={`fcp-num${edge(m)}`}>{MOIS[m - 1]}</th>)}
        <th className="fcp-num fcp-total">{p('Total')}</th>
      </tr>
    </thead>
  );
  const valRow = (key, label, f, cls = '', onClick = null) => (
    <tr key={key} className={cls} onClick={onClick || undefined}>
      <td className="fcp-lbl">{label}</td>
      {months.map((m) => <td key={m} className={`fcp-num${f(m) < 0 ? ' is-neg' : ''}${edge(m)}`}>{fmt(f(m))}</td>)}
      <td className="fcp-num fcp-total">{fmt(total(f))}</td>
    </tr>
  );
  /* Une ligne de coût : en saisie, ses mois se tapent ; sous règle, elle se
     lit avec sa règle, et un mois tapé la fige. */
  const setRule = (r, patch) => updateCostLine(tid, r.code, { ...patch, year }).then(() => load()).catch(() => {});
  /* LA VARIATION DE STOCK, CALCULÉE OU MANUELLE (décision Olivier,
     2026-09-30), quand les coûts sont « Selon plan » : calculée, elle
     compense ce qu'on pose sur les autres charges directes ; manuelle, elle
     se tape. La ligne du plan se dit « selon plan ». */
  const stockMode = (r) => {
    if (r.line_role === 'plan_cogs') return <span className="fcp-tag">{p('selon plan')}</span>;
    if (r.line_role !== 'stock_variation' || !view.cogs_aligned || !hasPlanCost) return null;
    const val = view.stock_var_mode === 'manual' ? 'manual' : 'calc';
    if (!canWrite) return <span className="fcp-tag">{val === 'manual' ? p('manuelle') : p('calculée')}</span>;
    /* UNE LISTE COMME LES AUTRES RÈGLES (demande d'Olivier, 2026-10-14) : même
       costume que « Vide (0) » sur la ligne Achats, plus une bascule à part. */
    return (
      <select className="pl-fc-select" value={val} onClick={(e) => e.stopPropagation()}
              aria-label={p('Variation de stock')}
              onChange={(e) => setStockVarMode(tid, year, e.target.value).then(() => load()).catch(() => {})}>
        <option value="calc">{p('Calculée')}</option>
        <option value="manual">{p('Manuelle')}</option>
      </select>
    );
  };
  const costRow = (r, label = r.label) => {
    const rule = r.computed ? p('calculée') : (method[r.code] ? p(RULE[method[r.code]] || 'règle') : null);
    const editable = canWrite && !locked(r);
    const manual = editable && !method[r.code];
    // La règle se choisit sur la ligne ; une ligne lue d'ailleurs (People,
    // plan d'investissement, calculée) ou saisie seulement n'en porte pas.
    const chip = canWrite && !(r.sales_locked || r.people_locked || r.capex_locked || r.computed || r.manual_only);
    return (
      <tr key={r.code}>
        {/* Libellé et mode sur UNE ligne, le mode au même x d'une ligne à
            l'autre (2026-09-29). */}
        <td className="fcp-lbl">
          <span className="pmc-lbl">
            <span className="pmc-lbl__txt">{label}</span>
            <span className="pmc-lbl__mode">
              {chip ? (
                <span className="pl-fc-inline" onClick={(e) => e.stopPropagation()}>
                  <MethodChip method={meta[r.code]?.forecast_method} rate={meta[r.code]?.forecast_rate}
                              baseId={meta[r.code]?.forecast_base_line_id} target={meta[r.code]?.forecast_target}
                              isCogs={r.section === 'cogs'} isOpex={r.section === 'other_opex'}
                              isPurchases={r.line_role === 'purchases'} revenueBase
                              choices={CHOICES}
                              lines={view.lines || []} selfId={r.code} onSet={(patch) => setRule(r, patch)} />
                </span>
              ) : stockMode(r) || (rule && <span className="fcp-tag">{rule}</span>)}
            </span>
          </span>
        </td>
        {months.map((m) => {
          const v = at(r, m);
          // Un mois réalisé se lit, il ne se tape pas.
          if (!editable || m <= closed) return <td key={m} className={`fcp-num${v < 0 ? ' is-neg' : ''}${edge(m)}`}>{fmt(v)}</td>;
          return (
            <td key={m} className={`fcp-num fcp-editcell${manual ? ' fcp-manual' : ''}${edge(m)}`}>
              <Cell value={v ? Math.round(Math.abs(v) / div) : null} type="number" canWrite placeholder="–"
                    format={(x) => (x == null ? '' : fmt(-x * div))} onSave={(x) => save(r, m, x)} />
            </td>
          );
        })}
        <td className="fcp-num fcp-total">{fmt(total((m) => at(r, m)))}</td>
      </tr>
    );
  };
  // La part du chiffre d'affaires, sous chaque total.
  const ca = (m) => at(revTotal, m);
  const pctOf = (v, base) => (!base || Math.abs(v) < 0.5 ? '–' : `${(v / base * 100).toFixed(1)} %`);
  const pctRow = (key, f) => (
    <tr key={key} className="pmc-pct">
      <td className="fcp-lbl">{p("% du chiffre d'affaires")}</td>
      {months.map((m) => <td key={m} className={`fcp-num${edge(m)}`}>{pctOf(f(m), ca(m))}</td>)}
      <td className="fcp-num fcp-total">{pctOf(total(f), total(ca))}</td>
    </tr>
  );
  // `subMode` : ce qui se règle SUR le total, à la place des modes des lignes.
  // Un sous-total qui porte un réglage à la place des modes des lignes.
  const subRow = (key, label, f, mode) => valRow(key, (
    <span className="pmc-lbl">
      <span className="pmc-lbl__txt">{label}</span>
      <span className="pmc-lbl__mode">{mode}</span>
    </span>
  ), f, 'fcp-sub');
  /* LE CALCUL DES COÛTS EST UN CHOIX DE MÉTHODE, pas une vue (demande d'Olivier,
     2026-10-14, proposition 2 du proto `bouton-calcul`) : deux méthodes nommées en
     radio, la retenue pleine. Il se pose sous la période, en tête de la carte
     des charges directes. */
  const aligned = Boolean(view.cogs_aligned && hasPlanCost);
  const choisir = (v) => setCostAlign(tid, year, v === 'aligned').then(() => load()).catch(() => {});
  const alignMode = !canWrite
    ? <span className="fcp-tag">{aligned ? p('selon plan') : p('libre')}</span> : (
      <span className="pmc-radio" role="radiogroup" aria-label={p('Calcul des coûts des produits vendus')}>
        {[
          { v: 'aligned', label: p('Selon le plan des ventes'), disabled: !hasPlanCost,
            title: hasPlanCost ? p('Aligné sur le coût des ventes calculé') : p('Le plan des ventes ne porte aucun coût.') },
          { v: 'free', label: p('Saisie libre') },
        ].map((o) => (
          <label key={o.v} className={`pmc-radio__o${(o.v === 'aligned') === aligned ? ' is-on' : ''}${o.disabled ? ' is-off' : ''}`} title={o.title}>
            <input type="radio" name="pmc-cogs-align" checked={(o.v === 'aligned') === aligned} disabled={o.disabled}
                   onChange={() => choisir(o.v)} />
            <i aria-hidden="true" />
            <span>{o.label}</span>
          </label>
        ))}
      </span>
    );
  const direct = [...goods, ...others];

  return (
    <div className="pmc">
      {part === 'direct' && (
      <CardShell title={p("Chiffre d'affaires et coût des ventes calculé")} subtitle={`${year} · ${uLbl}`} flush>
        <div className="fcp-wrap">
          <table className="fcp fcp--soft">
            {head}
            <tbody>
              {/* Le chiffre d'affaires se déplie d'un clic sur sa ligne ; le
                  repère suit le libellé, au survol seulement. */}
              {valRow('ca', <>{p("Chiffre d'affaires")}<span className={`pmc-chev${caOpen ? ' is-open' : ''}`} aria-hidden>›</span></>,
                (m) => at(revTotal, m), `fcp-sub pmc-clk${caOpen ? ' is-open' : ''}`, () => setCaOpen((o) => !o))}
              {caOpen && revLines.map((r) => valRow(r.code, r.label, (m) => at(r, m), 'pmc-kid'))}
              {valRow('plan', p('Coût des produits vendus du plan des ventes'),
                (m) => Number(view.plan_cogs?.[m - 1] || 0))}
            </tbody>
          </table>
        </div>
      </CardShell>
      )}
      {/* CHARGES DIRECTES, une seule carte (2026-09-29) : les coûts des
          produits vendus et leur sous-total, qui porte l'alignement, puis les
          autres coûts directs, puis le total de la section du compte de
          résultat. */}
      {/* LE CALCUL DES COÛTS DES PRODUITS VENDUS se choisit SOUS LE TITRE de la carte
          (demande d'Olivier, 2026-10-14) : à droite de l'entête, ce sont des vues ;
          ici, c'est une base de calcul. Il ne commande que ces lignes. */}
      {part === 'direct' && direct.length > 0 && (
        <CardShell title={p('Charges directes')} flush
                   subtitle={goods.length > 0 ? (
                     <span className="pmc-calc">
                       <span>{`${year} · ${uLbl}`}</span>
                       <span className="pmc-calc__ctl">
                         <span className="pmc-calc__lbl">{p('Coûts des produits vendus')}</span>
                         {alignMode}
                       </span>
                     </span>
                   ) : `${year} · ${uLbl}`}>
          <div className="fcp-wrap">
            <table className="fcp fcp--soft">
              {head}
              <tbody>
                {goods.map((r) => costRow(r))}
                {goods.length > 0 && subRow('cogs', p('Coûts des produits vendus'), (m) => sumOf(goods, m), null)}
                {goods.length > 0 && pctRow('cogs-pct', (m) => sumOf(goods, m))}
                {others.map((r) => costRow(r))}
                {valRow('sub', p('Charges directes'), (m) => sumOf(direct, m), 'fcp-sub')}
                {pctRow('pct', (m) => sumOf(direct, m))}
              </tbody>
            </table>
          </div>
        </CardShell>
      )}
      {part === 'variable' && opexLines.length > 0 && (
        <CardShell title={p("Coûts variables d'exploitation")} subtitle={`${year} · ${uLbl}`} flush>
          <div className="fcp-wrap">
            <table className="fcp fcp--soft">
              {head}
              <tbody>
                {/* Autant de lignes qu'on veut, aucune comprise : on ajoute un compte des autres
                    charges d'exploitation, on le retire par la croix (2026-10-22). */}
                {varRows.map((row) => costRow(row, (
                  <span className="fcp-varlbl">
                    {row.label}
                    {canWrite && (
                      <button type="button" className="lk-x lk-del" aria-label={p('Retirer')} title={p('Retirer des charges variables')}
                              onClick={() => updateCostLine(tid, row.code, { variable_cost: false }).then(() => load()).catch(() => {})}>✕</button>
                    )}
                  </span>
                )))}
                {varRows.length > 0 && pctRow('pct', (m) => varRows.reduce((t, r) => t + at(r, m), 0))}
                {canWrite && libres.length > 0 && (
                  <tr key="ajout">
                    <td className="fcp-lbl" colSpan={months.length + 2}>
                      <Choix value={null} canWrite emptyLabel={`＋ ${p('Ajouter une charge variable')}`}
                             options={libres.map((l) => ({ value: l.id, label: l.label }))}
                             onSave={(id) => id && updateCostLine(tid, id, { variable_cost: true }).then(() => load()).catch(() => {})} />
                    </td>
                  </tr>
                )}
                {varRows.length === 0 && !(canWrite && libres.length > 0) && (
                  <tr key="vide"><td className="fcp-lbl" colSpan={months.length + 2}>{p('Aucune charge variable')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardShell>
      )}
    </div>
  );
}
